import { Box, Button, Divider, TextField, Typography, useTheme, FormControlLabel, Checkbox } from '@mui/material'
import { isEmpty } from 'lodash'
import { useState } from 'react'
import { useFlag } from '@unleash/proxy-client-react'
import useAlerts from '../hooks/useAlerts'
import useBucketCreate from '../hooks/useBucketCreate'
import { BucketType } from '../../../graphql/generated'
import logger from '../../../utils/logger'
import useProfilePlan from '../hooks/useProfilePlan'

interface Props {
  modalTitle?: string
  modalDescription?: string
  onCancel?: () => void
}

export default function Create({ modalTitle = 'Create a New Bucket', onCancel }: Readonly<Props>) {
  const { createAlert } = useAlerts()
  const { mutation, loading } = useBucketCreate()
  const { isSharingEnabled } = useProfilePlan()
  const createNewGroupsLandingConfig = useFlag('createNewGroupsLandingConfig')
  const theme = useTheme()
  const [title, setTitle] = useState<string>('')
  const [bucketType] = useState(isSharingEnabled ? BucketType.Group : BucketType.Mybucket)
  const [isLiveEvent, setIsLiveEvent] = useState<boolean>(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTitle(e.target.value)
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (loading) {
      return
    }

    if (isEmpty(title)) {
      createAlert('Please enter a valid bucket name')
      return
    }

    mutation({
      onCompleted: (results) => {
        const bucketId = results.bucketCreate?.id

        // If this is a live event and feature flag is enabled, redirect to event customize page
        if (isLiveEvent && createNewGroupsLandingConfig) {
          window.location.href = `/bucket/${bucketId}/event-customize`
        } else {
          const suffix = bucketType === BucketType.Group ? '?inviteModal=true' : ''
          window.location.href = `/bucket/${bucketId}${suffix}`
        }
      },
      onError: (error) => {
        createAlert('There was an error creating your bucket')
        logger.error(error)
      },
      variables: { data: { title, bucketType } }
    })
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        maxWidth: 400,
        width: '100%',
        minWidth: {
          xs: '100%',
          sm: 450,
          md: 500
        }
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mb: 3,
          textAlign: 'center',
          fontWeight: 'bold'
        }}
      >
        {modalTitle}
      </Typography>

      <TextField
        fullWidth
        onChange={handleChange}
        label="Bucket Name"
        id="bucket-name"
        name="bucket-name"
        data-test="create-bucket-title"
        size="small"
        value={title}
        inputProps={{ 'data-testid': 'bucket-name' }}
        required
        sx={{
          mb: 1.5,
          '& .MuiInputLabel-root': {
            color: theme.palette.text.secondary
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: theme.palette.grey[300]
            }
          }
        }}
      />
      {createNewGroupsLandingConfig && (
        <FormControlLabel
          control={
            <Checkbox
              checked={isLiveEvent}
              onChange={(e) => setIsLiveEvent(e.target.checked)}
              size="small"
              sx={{
                color: theme.palette.primary.main,
                '&.Mui-checked': {
                  color: theme.palette.primary.main
                }
              }}
            />
          }
          label="This is a live event"
          sx={{
            py: 0,
            my: 0,
            '& .MuiTypography-root': {
              fontSize: '14px',
              color: theme.palette.text.primary
            }
          }}
        />
      )}
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: { xs: 1, sm: 2 }, mb: 0 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            borderRadius: 10,
            '&:hover': {
              borderColor: theme.palette.primary.dark,
              backgroundColor: 'transparent'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          data-testid="create-bucket-submit"
          type="submit"
          variant="contained"
          disabled={loading || !title}
          sx={{
            borderRadius: 10
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  )
}
